import React from "react";
import {
  MdCheckCircle,
  MdCancel,
  MdRemove,
  MdAssignmentTurnedIn,
  MdArrowForward,
} from "react-icons/md";
import { UserCheckIn } from "../../types/checkin";
import ModalShell from "../modals/ModalShell";
import { AdjustmentDecisionBadge, LifestyleZoneBadge } from "./checkInBadges";
import { formatDateLong } from "../../utils/dateUtils";
import {
  EMPTY_VALUE,
  formatLabels,
  formatOneDecimalWithUnit,
  formatSignedOneDecimalWithUnit,
  formatSignedPercent,
  signTone,
} from "../../utils/formatters";

interface CheckInDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  checkIn: UserCheckIn | null;
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <div className="mb-1 text-xs text-gray-500 dark:text-gray-400">{label}</div>
    <div className="text-sm text-navy-700 dark:text-white">{value}</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-6">
    <h4 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
      {title}
    </h4>
    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">{children}</div>
  </div>
);

// null is "not applicable" and must be distinguished from false
const ComplianceRow: React.FC<{ label: string; value: boolean | null }> = ({
  label,
  value,
}) => {
  let icon = <MdRemove className="h-5 w-5 text-gray-400" />;
  let text = "N/A";

  if (value === true) {
    icon = <MdCheckCircle className="h-5 w-5 text-green-500" />;
    text = "Compliant";
  } else if (value === false) {
    icon = <MdCancel className="h-5 w-5 text-red-500" />;
    text = "Not compliant";
  }

  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
      <span className="flex items-center gap-2 text-sm text-navy-700 dark:text-white">
        {icon}
        {text}
      </span>
    </div>
  );
};

const CheckInDetailsModal: React.FC<CheckInDetailsModalProps> = ({
  isOpen,
  onClose,
  checkIn,
}) => {
  if (!isOpen || !checkIn) return null;

  const snapshotEntries = Object.entries(
    checkIn.daily_consumption_snapshot || {}
  ).sort(([a], [b]) => a.localeCompare(b));

  const lifestyleScores: { label: string; value: number | null }[] = [
    { label: "Sleep", value: checkIn.lifestyle_score_sleep },
    { label: "Appetite", value: checkIn.lifestyle_score_appetite },
    { label: "Food Noise", value: checkIn.lifestyle_score_food_noise },
    { label: "Mood", value: checkIn.lifestyle_score_mood },
    { label: "Energy", value: checkIn.lifestyle_score_energy },
    {
      label: "Muscle Soreness",
      value: checkIn.lifestyle_score_muscle_soreness,
    },
  ];

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Check-In Details"
      icon={<MdAssignmentTurnedIn className="h-6 w-6 text-biafit-primary" />}
      size="3xl"
      layout="scroll"
      stickyHeader
    >
      <div className="px-6 py-6">
        <Section title="Summary">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Field
              label="Submitted"
              value={formatDateLong(checkIn.submitted_at)}
            />
            <Field label="Check-In ID" value={`#${checkIn.id}`} />
            <Field
              label="Closed Cycle"
              value={`#${checkIn.closed_nutrition_cycle_id}`}
            />
            <Field
              label="Generated Cycle"
              value={
                checkIn.generated_nutrition_cycle_id === null
                  ? "None"
                  : `#${checkIn.generated_nutrition_cycle_id}`
              }
            />
          </div>
        </Section>

        <Section title="Weight">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Field
              label="Average Weight"
              value={formatOneDecimalWithUnit(checkIn.average_weight_kg, "kg")}
            />
            <Field
              label="Rolling Average"
              value={formatOneDecimalWithUnit(
                checkIn.rolling_average_weight_kg,
                "kg"
              )}
            />
            <Field
              label="Change"
              value={
                <span className={signTone(checkIn.weight_change_pct)}>
                  {formatSignedPercent(checkIn.weight_change_pct) ??
                    EMPTY_VALUE}
                </span>
              }
            />
          </div>
        </Section>

        <Section title="Cycle Actuals">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Field
              label="Calories"
              value={formatOneDecimalWithUnit(
                checkIn.cycle_actual_kcal,
                "kcal"
              )}
            />
            <Field
              label="Protein"
              value={formatOneDecimalWithUnit(
                checkIn.cycle_actual_protein_g,
                "g"
              )}
            />
            <Field
              label="Carbs"
              value={formatOneDecimalWithUnit(checkIn.cycle_actual_carb_g, "g")}
            />
            <Field
              label="Fat"
              value={formatOneDecimalWithUnit(checkIn.cycle_actual_fat_g, "g")}
            />
          </div>
        </Section>

        <Section title="Compliance">
          <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            <ComplianceRow
              label="Calories"
              value={checkIn.is_calorie_compliant}
            />
            <ComplianceRow
              label="Protein"
              value={checkIn.is_protein_compliant}
            />
            <ComplianceRow label="Sodium" value={checkIn.is_sodium_compliant} />
            <ComplianceRow label="Fiber" value={checkIn.is_fiber_compliant} />
            <ComplianceRow
              label="Activity Consistency"
              value={checkIn.is_activity_consistent}
            />
            <ComplianceRow
              label="Menstrual Stability"
              value={checkIn.is_menstrual_stable}
            />
          </div>
        </Section>

        <Section title="Adjustment">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <AdjustmentDecisionBadge decision={checkIn.adjustment_decision} />
            {checkIn.safety_cap_applied && (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-semibold text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                Safety cap applied
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Reason"
              value={formatLabels(checkIn.adjustment_reason) || EMPTY_VALUE}
            />
            <Field
              label="Applied Delta"
              value={formatSignedOneDecimalWithUnit(
                checkIn.applied_kcal_delta,
                "kcal"
              )}
            />
            <Field
              label="Target Change"
              value={
                <span className="flex items-center gap-2">
                  {formatOneDecimalWithUnit(
                    checkIn.previous_target_kcal,
                    "kcal"
                  )}
                  <MdArrowForward className="h-4 w-4 text-gray-400" />
                  {formatOneDecimalWithUnit(checkIn.new_target_kcal, "kcal")}
                </span>
              }
            />
            <Field
              label="Maintenance Snapshot"
              value={
                checkIn.computed_maintenance_kcal_snapshot === null
                  ? "Not computed"
                  : formatOneDecimalWithUnit(
                      checkIn.computed_maintenance_kcal_snapshot,
                      "kcal"
                    )
              }
            />
          </div>
        </Section>

        <Section title="Lifestyle">
          <div className="mb-4">
            <LifestyleZoneBadge zone={checkIn.lifestyle_zone} suffix="zone" />
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {lifestyleScores.map((score) => (
              <Field
                key={score.label}
                label={score.label}
                value={
                  score.value === null || score.value === undefined
                    ? EMPTY_VALUE
                    : `${score.value}/5`
                }
              />
            ))}
          </div>
        </Section>

        <Section title="Daily Consumption Snapshot">
          {snapshotEntries.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No daily data recorded for this cycle.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    {[
                      "Date",
                      "Kcal",
                      "Protein",
                      "Carbs",
                      "Fat",
                      "Fiber",
                      "Sodium",
                      "Logs",
                    ].map((heading) => (
                      <th
                        key={heading}
                        className="pb-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {snapshotEntries.map(([date, entry]) => (
                    <tr
                      key={date}
                      className="border-b border-gray-200 last:border-0 dark:border-gray-700"
                    >
                      <td className="py-2 text-sm text-gray-600 dark:text-gray-300">
                        {formatDateLong(date)}
                      </td>
                      <td className="py-2 text-sm text-navy-700 dark:text-white">
                        {formatOneDecimalWithUnit(entry.kcal)}
                      </td>
                      <td className="py-2 text-sm text-navy-700 dark:text-white">
                        {formatOneDecimalWithUnit(entry.protein_g, "g")}
                      </td>
                      <td className="py-2 text-sm text-navy-700 dark:text-white">
                        {formatOneDecimalWithUnit(entry.carb_g, "g")}
                      </td>
                      <td className="py-2 text-sm text-navy-700 dark:text-white">
                        {formatOneDecimalWithUnit(entry.fat_g, "g")}
                      </td>
                      <td className="py-2 text-sm text-navy-700 dark:text-white">
                        {formatOneDecimalWithUnit(entry.fiber_g, "g")}
                      </td>
                      <td className="py-2 text-sm text-navy-700 dark:text-white">
                        {formatOneDecimalWithUnit(entry.sodium_mg, "mg")}
                      </td>
                      <td className="py-2 text-sm text-gray-600 dark:text-gray-300">
                        {entry.log_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      </div>
    </ModalShell>
  );
};

export default CheckInDetailsModal;
